import { CommonModule } from '@angular/common';
import {
  Component, OnDestroy, OnInit, AfterViewInit, Renderer2, HostListener, ElementRef, ViewChild, Optional
} from '@angular/core';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { AppRoutingService } from '@core/app-routing.service';
import { AppConfigService } from '@core/appconfig.service';
import { MasqueradeService } from '@core/services/masquerade/masquerade.service';
import { UserService } from '@core/user.service';
import { Classroom } from '@models/classroom';
import { NavigationMenuItem } from '@models/navigation-menu-item';
import { IProductLine } from '@models/product-line';
import { IProductLineVariant } from '@models/product-variant';
import { School } from '@models/school';
import { IUser } from '@models/user';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { FeedbackService } from '@services/feedback/feedback.service';
import { ButtonComponent } from '@shared/button/button.component';
import { CompanyCode } from '@shared/enums/company-code';
import { RoleType } from '@shared/enums/role-type';
import { Helpers } from '@shared/helpers';
import { BrandedWistiaComponent } from '@shared/wistia/branded-wistia/branded-wistia.component';
import { ZbpIconComponent } from '@shared/zbp-icon/zbp-icon';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { combineLatest, distinctUntilChanged, map, Observable, Subscription, } from 'rxjs';
import { LearningFacade } from 'src/app/classrooms/store/learning.facade';
import { ParentFacade } from 'src/app/parents/store/parent.facade';
import { TeacherService } from 'src/app/teachers/teachers-service';
import { HeaderService } from '../header-service';
import { DISTRICT_ADMIN_MANAGE_MENU_ID } from './primary-menu-builders/district-admin-menu';
import {
  buildHelpDropdownMenuItems, getIntroVideoWistiaCode, HELP_MENU_ID
} from './primary-menu-builders/help-menu';
import { PARENT_CHILDREN_MENU_ID } from './primary-menu-builders/parent-menu';
import { SCHOOL_ADMIN_MANAGE_MENU_ID } from './primary-menu-builders/school-admin-menu';
import { TEACHERS_CLASSES_MENU_ID, TEACHERS_PRODUCTS_MENU_ID } from './primary-menu-builders/teacher-menu';
import { SubnavigationComponent } from './subnavigation/subnavigation.component';

@Component({
  selector: 'zbp-branded-header',
  templateUrl: './branded-header.component.html',
  styleUrls: ['./branded-header.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ButtonComponent,
    NgbTooltipModule,
    SubnavigationComponent,
    ZbpIconComponent,
  ]
})
export class BrandedHeaderComponent implements OnDestroy, OnInit, AfterViewInit {
  primaryMenuItems: NavigationMenuItem[] = this.headerService.currentMenus ?? [];
  dropdownMenuItems: NavigationMenuItem[] = [];
  helpDropdownMenuItems: NavigationMenuItem[] = buildHelpDropdownMenuItems(() => this.openIntroVideo());
  products: IProductLine[] = this.headerService.currentMenuTeacherProducts;
  user: IUser = null;
  adminSchools: School[] = []; // only for school admins, used to determine if the admin has multiple schools
  user$ = this.userService.user$;
  isMasqueraded: boolean = false;
  isHighlightsPortalUser: boolean = false;
  brandLogoData = null;
  brand: string = CompanyCode.HighlightsPortal.toLowerCase();
  queryParams$: Observable<Params> = this.route.queryParams;
  isTeacher$: Observable<boolean> = this.user$.pipe(map(user => user.isTeacher));
  isSchoolAdmin$: Observable<boolean> = this.user$.pipe(map(user => user.isSchoolAdmin));
  isDistrictAdmin$: Observable<boolean> = this.user$.pipe(map(user => user.isDistrictAdmin));
  isElevatedAdmin$: Observable<boolean> = this.user$.pipe(map(user => user.isElevatedAdmin));
  isParent$: Observable<boolean> = this.user$.pipe(map(user => user.isParent));
  teacherProductsMenuId = TEACHERS_PRODUCTS_MENU_ID;
  teacherClassesMenuId = TEACHERS_CLASSES_MENU_ID;
  parentChildrenMenuId = PARENT_CHILDREN_MENU_ID;
  districtAdminManageMenuId = DISTRICT_ADMIN_MANAGE_MENU_ID;
  schoolAdminManageMenuId = SCHOOL_ADMIN_MANAGE_MENU_ID;
  helpMenuId = HELP_MENU_ID;
  iconNames = zbpIconNames;
  classrooms: Classroom[] = [];
  classroomSubscription;

  isMenuOpen = false;
  @ViewChild('menuToggle') menuToggleRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('navMenu') navMenuRef!: ElementRef<HTMLElement>;
  @ViewChild('headerLeft') headerLeftRef!: ElementRef<HTMLElement>;

  isPortrait = false;
  private portraitQuery = matchMedia('(orientation: portrait)');
  private listener = (e: MediaQueryListEvent) => {
    this.isPortrait = e.matches;

    if (this.isPortrait) {
      this.headerLeftRef.nativeElement.appendChild(this.menuToggleRef.nativeElement);
    } else {
      this.navMenuRef.nativeElement.appendChild(this.menuToggleRef.nativeElement);
    }
  };

  private subscriptions: Subscription[] = [];
  private expandedMenuItemId: string = null;

  private headerLogoDataByBrand = {
    [CompanyCode.HighlightsPortal]: {
      src: 'assets/img/highlights.svg',
      altTranslationKey: 'header-HLLogo'
    },
    [CompanyCode.ZBPortal]: {
      src: 'assets/img/zb-icon-white.svg',
      altTranslationKey: 'header-ZBLogo'
    },
  };

  @HostListener('window:resize')
  onResize() {
    this.turnMenuOff();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
      this.menuToggleRef?.nativeElement?.focus();
    }
  }

  /**
   * Builds header menu items based on user's RoleType
   *
   * Menu items include the dropdown menus and primary menu items
   *
   * Calls HeaderService to configure menu items
   *
   * Can be triggered by providing a true value for HeaderService's refreshHeader$ Subject
   */
  buildHeaderMenu$: Observable<NavigationMenuItem[]> = combineLatest([
    this.isDistrictAdmin$,
    this.isSchoolAdmin$,
    this.isTeacher$,
    this.isElevatedAdmin$,
    this.isParent$,
    this.headerService.refreshHeader$,
    this.headerService.hasRosterIntegrations$,
  ])
    .pipe(
      map(([isDistrictAdmin, isSchoolAdmin, isTeacher, isElevatedAdmin, isParent]) => {
        if (this.headerService.displayPrimaryHeaderMenus) {
          if (isDistrictAdmin) {
            this.primaryMenuItems = this.headerService.buildDistrictAdminMenus();
            this.dropdownMenuItems = this.headerService.getDropdownMenu(RoleType.DistrictAdministrator);
          } else if (isSchoolAdmin) {
            this.primaryMenuItems = this.headerService.buildSchoolAdminMenus();
            this.dropdownMenuItems = this.headerService.getDropdownMenu(RoleType.SchoolAdministrator);
          } else if (isTeacher) {
            this.primaryMenuItems = this.headerService.buildTeacherPrimaryMenus();
          } else if (isElevatedAdmin) {
            // All Elevated Admin menu items are built by the AdminComponent directly, then added to the header service
            // Elevated Admin has not been redesigned
            const currentHeaderServiceMenus = this.headerService.currentMenus;
            this.primaryMenuItems =  Helpers.sortByWeight(currentHeaderServiceMenus);
            this.headerService.currentMenus = this.primaryMenuItems;
          } else if (isParent) {
            this.primaryMenuItems = this.headerService.buildParentMenus();
          }

          return this.primaryMenuItems;
        }

        this.headerService.currentMenus = [];
        this.primaryMenuItems = [];
        this.products = [];

        return [];
      }),
      distinctUntilChanged(),
    );

  get userNameSchoolId(): string {
    const value = this.user.getDisplayName;

    if (this.user.abbreviatedSchoolId) {
      return `${value} (${this.user.abbreviatedSchoolId})`;
    }
    return value;
  }

  get canSelfAssignLicenses(): boolean {
    return this.user.isTeacher
      && this.user.profileDetail
      && this.user.profileDetail.canManageLicenses;
  }

  get canCreateClass(): boolean {
    return this.user.profileDetail?.canManageClassroom
    && this.user.profileDetail?.canManageSelf;
  }

  get helpMenu(): NavigationMenuItem | undefined {
    return this.primaryMenuItems.find(m => m.id === 'contactUsMenuLink');
  }

  primaryMenuItemClicked(event: Event, primaryHeaderMenu: HTMLElement) {
    event.preventDefault();

    // reset the border to be rounded on all corners
    this.renderer.removeClass(primaryHeaderMenu, 'submenu-expanded');
    this.expandedMenuItemId = null;
  }

  chooseRoleClick(event: Event) {
    event.preventDefault();
    this.headerService.chooseRoleClick();
  }

  licensesClicked(headerMenu: HTMLElement, dropdownItem: HTMLElement) {
    this.closeMenu(headerMenu, dropdownItem);
  }

  /**
   * Handles expanding and collapsing dropdown menu items that have submenus.
   */
  expandSubmenu(
    event: Event, primaryHeaderMenu: HTMLElement, dropdownItem: HTMLElement, isProductsDropdown: boolean = false
  ) {
    let handleSubmenu = true;

    if (event.type === 'keydown') {
      const { key } = event as KeyboardEvent;

      if (key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();

        this.removeActiveMenuStyling(primaryHeaderMenu, dropdownItem, isProductsDropdown);
        dropdownItem.focus();
        return;
      }

      if (key !== 'Enter' && key !== ' ' && key !== 'ArrowRight') {
        handleSubmenu = false;
        return;
      }
    }

    event.preventDefault();
    event.stopPropagation();

    if (handleSubmenu) {
      const isMenuItemAlreadyExpanded = this.expandedMenuItemId === dropdownItem.id;

      // Reset all previous styling for the menu and submenus
      this.removeActiveMenuStyling(primaryHeaderMenu, dropdownItem, isProductsDropdown);

      // If the menu item that was clicked isn't already expanded, then open the submenu
      if (!isMenuItemAlreadyExpanded) {
        this.expandedMenuItemId = dropdownItem.id;
        // removes rounded corners on right side of products-menu dropdown
        this.renderer.addClass(primaryHeaderMenu, 'submenu-expanded');
        this.renderer.addClass(dropdownItem, 'show');
        if (isProductsDropdown) {
          this.renderer.addClass(dropdownItem.nextElementSibling, 'show');
        } else {
          this.renderer.addClass(dropdownItem.firstElementChild, 'show');
        }
        dropdownItem.ariaExpanded = 'true';

        if ((event as KeyboardEvent).key === 'ArrowRight') {
          setTimeout(() => {
            const firstItem = isProductsDropdown
              ? dropdownItem.parentElement.querySelector<HTMLElement>('.dropdown-submenu a.dropdown-item')
              : dropdownItem.querySelector<HTMLElement>('.dropdown-submenu a.dropdown-item');
            firstItem?.focus();
          });
        }
      }
    }
  }

  isSingleAdmin(menu) {
    return menu.id === this.districtAdminManageMenuId
      || (menu.id === this.schoolAdminManageMenuId && this.adminSchools.length <= 1);
  }

  isMultiAdmin(menu) {
    return menu.id === this.schoolAdminManageMenuId && this.adminSchools.length > 1;
  }

  isRouteActive(value: string): boolean {
    return this.router.url === value;
  }

  private removeActiveMenuStyling(
    primaryHeaderMenu: HTMLElement, submenuItem: HTMLElement,
    isProductsDropdown: boolean = false
  ) {
    this.expandedMenuItemId = null;
    primaryHeaderMenu.ariaExpanded = 'false';
    if (isProductsDropdown) {
      this.renderer.addClass(submenuItem.nextElementSibling, 'show');
    } else {
      this.renderer.addClass(submenuItem.firstElementChild, 'show');
    }
    this.renderer.removeClass(primaryHeaderMenu, 'submenu-expanded');

    primaryHeaderMenu.querySelectorAll('.has-submenu').forEach((menu) => {
      menu.ariaExpanded = 'false';
      this.renderer.removeClass(menu, 'show');
      if (isProductsDropdown) {
        this.renderer.addClass(menu.nextElementSibling, 'show');
      } else {
        this.renderer.addClass(menu.firstElementChild, 'show');
      }
    });
  }

  /**
   * Should be added on any dropdown in the Primary navigation bar.
   *
   * Closes primary menu dropdown and removes all styling from any open products/submenus
   * Clicking outside of menu will automatically close menu & items
   * Clicking on the link for the current page requires this to be reset manually
   */
  private closeMenu(primaryHeaderMenu: HTMLElement, navigationDropdownMenu: HTMLElement) {
    this.expandedMenuItemId = null;
    this.renderer.removeClass(primaryHeaderMenu, 'show');
    this.renderer.removeClass(primaryHeaderMenu, 'submenu-expanded');
    this.renderer.removeClass(navigationDropdownMenu, 'show');
    if (navigationDropdownMenu.firstElementChild) {
      this.renderer.removeClass(navigationDropdownMenu.firstElementChild, 'show');
    }

    primaryHeaderMenu.ariaExpanded = 'false';
  }

  clickedProductSubMenuItem(
    event: Event,
    product: IProductLine,
    variant: IProductLineVariant,
    primaryHeaderMenu: HTMLElement,
    navigationDropdownMenu: HTMLElement,
  ) {
    const handleSubmenu = this.handleSubmenuKeyboardEvent(event, primaryHeaderMenu);

    if (handleSubmenu) {
      this.closeMenu(primaryHeaderMenu, navigationDropdownMenu);
      this.headerService.navigateToProduct(product, variant, this.user.isTeacher);
    }
  }

  goHome(event: MouseEvent): boolean {
    event.preventDefault();
    if (this.headerService.logoClickedFunction) {
      this.headerService.logoClickedFunction();
    }

    return false;
  }

  newClass(primaryHeaderMenu: HTMLElement, navigationDropdownMenu: HTMLElement) {
    this.closeMenu(primaryHeaderMenu, navigationDropdownMenu);
    this.learningFacade.selectClassroom(null);
    const schoolId = this.teacherService.currentSchoolId$.value ?? this.user.schools[0].schoolId;
    this.router.navigate(
      this.appRoutingService.getTeachersAddClassRoute(schoolId).toArray()
    );
  }

  handleSubmenuKeyboardEvent(event: Event, primaryHeaderMenu: HTMLElement): boolean {
    let handleSubmenu: boolean = true;

    if (event.type === 'keydown') {
      const { key } = event as KeyboardEvent;

      if (key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();

        const submenuParent = (event.currentTarget as HTMLElement).parentElement.parentElement.parentElement
          .querySelector('.has-submenu') as HTMLElement;

        this.removeActiveMenuStyling(primaryHeaderMenu, submenuParent ?? undefined, true);
        submenuParent?.focus();

        return false;
      }

      if (key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();

        return false;
      }

      if (key === 'ArrowLeft') {
        event.preventDefault();
        event.stopPropagation();

        const submenuParent = (event.currentTarget as HTMLElement).parentElement.parentElement.parentElement
          .querySelector('.has-submenu') as HTMLElement;

        this.removeActiveMenuStyling(primaryHeaderMenu, submenuParent ?? undefined, true);
        submenuParent?.focus();
        return false;
      }

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();

        const submenu = (event.currentTarget as HTMLElement)
          .closest('.dropdown-submenu') as HTMLElement;

        const items = Array.from(
          submenu.querySelectorAll<HTMLElement>('a.dropdown-item')
        );

        const currentIndex = items.indexOf(event.currentTarget as HTMLElement);

        if (key === 'ArrowDown') {
          items[currentIndex + 1]?.focus();
        } else {
          items[currentIndex - 1]?.focus();
        }
        return false;
      }

      if (key !== 'Enter' && key !== ' ' && key !== 'ArrowRight') {
        handleSubmenu = false;
        return handleSubmenu;
      }
    }

    event.preventDefault();
    event.stopPropagation();

    return handleSubmenu;
  }

  handleManageNavigation(
    event: Event, adminManageOption: NavigationMenuItem, primaryHeaderMenu: HTMLElement,
    navigationDropdownMenu: HTMLElement
  ) {
    const handleSubmenu = this.handleSubmenuKeyboardEvent(event, primaryHeaderMenu);


    if (!adminManageOption.disabled && handleSubmenu) {
      this.closeMenu(primaryHeaderMenu, navigationDropdownMenu);
      // Only run onClickFunction if not isRouted (this is matching existing ZB functionality)

      if (adminManageOption.isRouted) {
        this.headerService.navigateToManageMenuDropdownItem(event, adminManageOption);
      } else {
        adminManageOption.onClickFunction();
      }
    }
  }

  handleClassesNavigation(
    event: Event, classroomId: string, primaryHeaderMenu: HTMLElement,
    navigationDropdownMenu: HTMLElement
  ) {
    event.preventDefault();
    this.closeMenu(primaryHeaderMenu, navigationDropdownMenu);
  }

  /**
   * Closes the Help dropdown when one of its options is selected.
   *
   * Routed (external) links open in a new tab with default anchor behavior.
   * Other links run their onClickFunction.
   */
  handleHelpDropdownNavigation(
    event: Event, helpOption: NavigationMenuItem, primaryHeaderMenu: HTMLElement,
    navigationDropdownMenu: HTMLElement
  ): void {
    this.closeMenu(primaryHeaderMenu, navigationDropdownMenu);

    if (!helpOption?.isRouted && helpOption?.onClickFunction) {
      event.preventDefault();
      // If the onClickFunction opens a modal, the Help button will be focused when the modal closes.
      primaryHeaderMenu.querySelector<HTMLElement>(`#${this.helpMenuId}MenuLink`)?.focus();
      helpOption.onClickFunction();
    }
  }

  openIntroVideo(): void {
    const modalRef = this.modalService.open(BrandedWistiaComponent, {
      size: 'lg',
      modalDialogClass: 'transparent-bg',
      // Added to modal for a11y (modal has a 'dialog' role).
      ariaLabelledBy: 'screenReaderNameElement',
    });
    modalRef.componentInstance.isModal = true;
    modalRef.componentInstance.showErrorMessage = true;
    modalRef.componentInstance.accessibleName = 'Introduction Video';
    modalRef.componentInstance.wistiaCode = getIntroVideoWistiaCode(
      this.user, this.userService.teacherTutorialWistiaCode
    );
  }

  handleParentClassroomNavigation(
    event: Event, primaryHeaderMenu: HTMLElement,
    navigationDropdownMenu: HTMLElement,
    studentUserId: string, classroomId: string
  ): void {
    const handleSubmenu = this.handleSubmenuKeyboardEvent(event, primaryHeaderMenu);

    if (handleSubmenu) {
      this.closeMenu(primaryHeaderMenu, navigationDropdownMenu);
      this.headerService.navigateToStudentClassroom(studentUserId, classroomId);
    }
  }

  handleParentNoClassroomNavigation(
    event: Event, primaryHeaderMenu: HTMLElement,
    navigationDropdownMenu: HTMLElement, studentUserId: string
  ): void {
    const handleSubmenu = this.handleSubmenuKeyboardEvent(event, primaryHeaderMenu);

    if (handleSubmenu) {
      this.closeMenu(primaryHeaderMenu, navigationDropdownMenu);
      this.router.navigate(this.appRoutingService.getParentNoClassroomForStudentRoute(studentUserId).toArray());
    }
  }

  verifyStopMasquerade() {
    const stopMasqueradeModal = this.masqueradeService.confirmStopMasqueradeModal().subscribe();

    this.subscriptions.push(stopMasqueradeModal);
  }

  openFeedbackForm() {
    this.subscriptions.push(
      this.feedbackService.openFeedbackForm().subscribe()
    );
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  turnMenuOff() {
    if (this.isMenuOpen) this.isMenuOpen = false;
  }

  onDropdownFocusOut(event: FocusEvent, primaryHeaderMenu: HTMLElement, navigationDropdownMenu: HTMLElement): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!navigationDropdownMenu.contains(relatedTarget)) {
      this.closeMenu(primaryHeaderMenu, navigationDropdownMenu);
    }
  }

  readonly teacherLicensesRoute: string;

  constructor(
    private router: Router,
    private appConfig: AppConfigService,
    private userService: UserService,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    public headerService: HeaderService,
    private masqueradeService: MasqueradeService,
    public modalService: NgbModal,
    public learningFacade: LearningFacade,
    private teacherService: TeacherService,
    private appRoutingService: AppRoutingService,
    private feedbackService: FeedbackService,
    @Optional() private parentFacade: ParentFacade, // Parent Facade is only needed in ParentsModule
  ) {
    this.teacherLicensesRoute = this.appRoutingService.getTeachersLicensesRoute().toString();
  }

  ngOnInit(): void {
    this.brand = this.appConfig.loginBrand;
    this.brandLogoData = this.headerLogoDataByBrand[this.brand];
    this.isHighlightsPortalUser = this.brand === CompanyCode.HighlightsPortal;
    this.user = this.userService.user;
    this.adminSchools = this.headerService.schools.filter(
      school => school.roleTypes.includes(RoleType.SchoolAdministrator));
    this.isMasqueraded = this.userService.isMasqueraded;

    this.subscriptions.push(
      this.buildHeaderMenu$.subscribe(),
    );

    // ParentFacade is only a provider in ParentsModule.
    // This subscription needs to live in this component instead of HeaderService as HeaderService is Root provided.
    if (this.parentFacade) {
      this.subscriptions.push(
        this.parentFacade.studentContext$.pipe(
          distinctUntilChanged((a, b) => a?.userId === b?.userId),
        ).subscribe((student) => {
          this.headerService.parentStudentContext = student;
          this.headerService.refreshHeader$.next(true);
        })
      );
    } else {
      this.headerService.refreshHeader$.next(true);
    }

  }

  ngAfterViewInit(): void {
    this.listener({ matches: this.portraitQuery.matches } as MediaQueryListEvent);

    this.isPortrait = this.portraitQuery.matches;
    this.portraitQuery.addEventListener('change', this.listener);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());

    this.portraitQuery.removeEventListener('change', this.listener);
  }

  protected readonly zbpIconNames = zbpIconNames;
}
